import type { LoggerService } from '@kerberos-compliance/compliance-shared-ui';

/**
Storage prefix owned by this application (see `StorageConfigurationToken`).
 */
const appStoragePrefix = 'ad_';

/**
The Azure access token the pre-Hanko login persisted under the app prefix.
 */
const legacyAzureTokenKey = `${appStoragePrefix}accessTokenAzure`;

/**
Prefix of every entry MSAL wrote into browser storage for its own token cache.
 */
const msalCachePrefix = 'msal.';

/**
 * Reads all keys of a storage area. Returns an empty list when storage is
 * unavailable (private mode, blocked cookies) so callers never have to guard.
 *
 * @param {Storage | undefined} storage - The storage area to enumerate.
 * @returns {string[]} The keys currently present.
 */
const readKeys = (storage: Storage | undefined): string[] => {
  if (!storage) {
    return [];
  }
  try {
    return Object.keys(storage);
  } catch {
    return [];
  }
};

/**
 * Whether the given key belongs to a pre-Hanko Azure session.
 *
 * @param {string} key - The storage key to classify.
 * @returns {boolean} True for the legacy Azure token and for MSAL cache entries.
 */
const isAzureArtifact = (key: string): boolean =>
  key === legacyAzureTokenKey || key.startsWith(msalCachePrefix) || key.includes(`.${msalCachePrefix}`);

/**
 * Removes a key, ignoring failures — a partially cleaned storage is still better
 * than aborting the whole purge.
 *
 * @param {Storage | undefined} storage - The storage area to write to.
 * @param {string} key - The key to remove.
 */
const removeKey = (storage: Storage | undefined, key: string): void => {
  try {
    storage?.removeItem(key);
  } catch {
    // Non-critical — continue purging the remaining keys.
  }
};

/**
 * ⚠️ TRANSITIONAL (KCP-1236) — delete once no browser can still be carrying an
 * Azure session, i.e. after one release cycle beyond the Hanko cutover. Removing
 * it early is harmless for anyone who has logged in since.
 *
 * Detects and removes the browser-storage residue of a pre-Hanko Azure B2C
 * session, so a returning user is handed to the Hanko login with a clean slate
 * instead of resuming a session no longer backed by an identity provider.
 *
 * An Azure-established session would technically keep working until its ADP
 * refresh token expires — the profile page and the security nudge already hide
 * themselves for a non-Hanko session — but it would do so with no credential
 * management and would force a re-login at an arbitrary later moment. Ending it
 * here makes the cutover a single, scheduled event instead of a long tail, and
 * clears MSAL's own token cache along with it.
 *
 * Detection keys off artifacts only the Azure flow ever wrote
 * (`ad_accessTokenAzure`, the `msal.*` cache), so Hanko and impersonation
 * sessions are never touched, and once purged the check costs nothing on
 * subsequent loads.
 *
 * @param {LoggerService} logger - Used to record that a legacy session was discarded.
 * @returns {boolean} `true` when a legacy Azure session was found and purged.
 */
export const didPurgeLegacyAzureSession = (logger: LoggerService): boolean => {
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  const local = globalThis.localStorage as Storage | undefined;
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  const session = globalThis.sessionStorage as Storage | undefined;

  const localKeys = readKeys(local);
  const sessionKeys = readKeys(session);

  const hasAzureSession = [...localKeys, ...sessionKeys].some((key) => isAzureArtifact(key));
  if (!hasAzureSession) {
    return false;
  }

  logger.warn(
    '[didPurgeLegacyAzureSession] Azure B2C session residue detected. Clearing it and requiring a fresh login.',
  );

  // Drop the whole app-owned namespace, not just the Azure keys: the platform
  // token, grants and cached organization state all came from that session and
  // would otherwise outlive the identity that produced them.
  for (const key of localKeys) {
    if (key.startsWith(appStoragePrefix) || isAzureArtifact(key)) {
      removeKey(local, key);
    }
  }
  for (const key of sessionKeys) {
    if (key.startsWith(appStoragePrefix) || isAzureArtifact(key)) {
      removeKey(session, key);
    }
  }

  return true;
};
